/**
 * Centralised navigation configuration for the Helix sidebar.
 *
 * Instead of four hardcoded arrays scattered inside Sidebar.tsx, every
 * navigation item is defined once in a registry (`NAV_ITEMS`) and then
 * referenced by key in the per-mode ordering arrays. The public
 * `buildNavigation()` function selects the right ordering at runtime.
 */

import {
  LayoutDashboard,
  TrendingUp,
  Settings,
  Calendar,
  ShoppingCart,
  CheckCircle,
  Users,
  FileText,
  History,
  Newspaper,
  UserCog,
  Upload,
  Truck,
  Database,
  PieChart,
  Box,
  Factory,
  Package,
  CalendarClock,
  Gauge,
  Coins,
} from 'lucide-react';
import type { MenuVisibility } from '../contexts/MenuSettingsContext';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FeatureFlag =
  | 'delivery_appeals'
  | 'spend_leakage'
  | 'bulk_upload'
  | 'commodity_codes'
  | 'customer_stock_levels'
  | 'switch_call_off_tab'
  | 'po_line_schedules'
  | 'schedule_collaboration'
  | 'allow_customer_decline'
  | 'job_documents';

/** Shape consumed by the Sidebar rendering logic (unchanged from old code). */
export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  children?: NavItem[];
  visibilityKey?: keyof MenuVisibility;
  /**
   * Optional customer-side override. When the user is viewing the customer
   * sidebar, buildNavigation swaps `visibilityKey` → `customerVisibilityKey`
   * so the item is gated by its own admin toggle instead of sharing state
   * with the supplier-side key.
   */
  customerVisibilityKey?: keyof MenuVisibility;
  tourId?: string;
  featureFlag?: FeatureFlag;
  /** Alternate label when portal context is flipped (Supplier Portal mode) */
  flipName?: string;
}

// ---------------------------------------------------------------------------
// Item registry
// ---------------------------------------------------------------------------
// Each unique navigation entry is defined exactly once. Items that differ
// between portal modes (different label, icon, etc.) are stored as separate
// keys.
// ---------------------------------------------------------------------------

const NAV_ITEMS = {
  // ---- Common ----
  dashboard: {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    visibilityKey: 'dashboard',
    customerVisibilityKey: 'customerDashboard',
    tourId: 'dashboard',
  },
  documents: {
    name: 'Documents',
    href: '/documents',
    icon: FileText,
    visibilityKey: 'documents',
    customerVisibilityKey: 'customerDocuments',
  },

  // ---- Standard supplier ----
  customerPerformance: {
    name: 'Customer Performance',
    href: '/customers/performance',
    icon: TrendingUp,
    visibilityKey: 'customerPerformance',
  },
  // Supplier-sites-only: monthly OTD scorecard per site (customers never see it
  // — it is absent from the customer ordering AND the API returns empty for
  // customer views).
  sitePerformance: {
    name: 'Site Performance',
    href: '/site-performance',
    icon: Gauge,
    visibilityKey: 'sitePerformance',
  },
  reportsSupplier: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  calendarSupplier: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'calendar',
  },
  purchasing: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchasing',
    tourId: 'purchasing',
  },
  partsMaster: {
    name: 'Parts Master',
    href: '/parts-master',
    icon: Package,
    visibilityKey: 'partsMaster' as keyof MenuVisibility,
  },
  partSchedule: {
    name: 'Part Schedule',
    href: '/part-schedule',
    icon: CalendarClock,
    visibilityKey: 'partSchedule' as keyof MenuVisibility,
  },
  qualitySupplier: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  customers: {
    name: 'Customers',
    href: '/customers',
    icon: Users,
    visibilityKey: 'customers',
    tourId: 'customers',
  },
  // Read-only directory of the people at the viewer's own company (suppliers
  // see their org's users, customers see theirs). No visibilityKey → always on.
  users: {
    name: 'Users',
    href: '/users',
    icon: UserCog,
  },
  userInvitationsSupplier: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  userActivityHistory: {
    name: 'User Activity History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
  },
  newsReleaseSupplier: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'newsRelease',
  },
  dataImport: {
    name: 'Data Import',
    href: '/data-import',
    icon: Database,
    visibilityKey: 'dataImport' as keyof MenuVisibility,
  },
  importMonitor: {
    name: 'Import Monitor',
    href: '/import-monitor',
    icon: Upload,
  },
  // Operational monitor for the ERP currency feed. Admin-only: the rate
  // history endpoint 403s non-admins, so a regular user would only see
  // empty tables.
  exchangeRates: {
    name: 'Exchange Rates',
    href: '/admin/exchange-rates',
    icon: Coins,
    visibilityKey: 'exchangeRates',
  },
  settings: {
    name: 'My Account',
    href: '/settings',
    icon: Settings,
  },
  // ---- Standard customer ----
  salesOrders: {
    name: 'Purchase Orders',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchaseOrders',
    tourId: 'purchasing',
  },
  qualityCustomer: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  suppliers: {
    name: 'Suppliers',
    href: '/suppliers',
    icon: Users,
    visibilityKey: 'customerSuppliers' as keyof MenuVisibility,
    tourId: 'suppliers',
  },
  calendarCustomer: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'customerCalendar' as keyof MenuVisibility,
  },
  userInvitationsCustomer: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  companyProfile: {
    name: 'Company Profile',
    href: '/customers',
    icon: Factory,
    visibilityKey: 'companyProfile',
  },
  newsReleaseCustomer: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'customerNewsRelease' as keyof MenuVisibility,
  },
  reportsCustomer: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  stockLevels: {
    name: 'Stock Levels',
    href: '/stock-levels',
    icon: Box,
    featureFlag: 'customer_stock_levels' as FeatureFlag,
  },

  // ---- Logistics ----
  logisticsCallOffs: {
    name: 'Dispatched Call-Offs',
    href: '/logistics/call-offs',
    icon: Truck,
  },
} satisfies Record<string, Omit<NavItem, 'children'> & { children?: Omit<NavItem, 'children'>[] }>;

type NavKey = keyof typeof NAV_ITEMS;

// ---------------------------------------------------------------------------
// Ordered sequences per mode
// ---------------------------------------------------------------------------
// Each array lists the registry keys in the exact display order that the
// old hardcoded arrays used. Changing the order here changes the sidebar.
// ---------------------------------------------------------------------------

const STANDARD_SUPPLIER_ORDER: NavKey[] = [
  'dashboard',
  'sitePerformance',
  'customerPerformance',
  'reportsSupplier',
  'calendarSupplier',
  'purchasing',
  'partsMaster',
  'partSchedule',
  'qualitySupplier',
  'customers',
  'users',
  'documents',
  'newsReleaseSupplier',
  'userActivityHistory',
  'dataImport',
  'importMonitor',
  'exchangeRates',
];

const STANDARD_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'salesOrders',
  'calendarCustomer',
  'qualityCustomer',
  'suppliers',
  'users',
  'documents',
  'newsReleaseCustomer',
  'stockLevels',
];

// CR-4143: Compliance module — onboarding/questionnaire modules removed; the
// compliance view now surfaces only customers/reports/documents.
const COMPLIANCE_SUPPLIER_ORDER: NavKey[] = [
  'dashboard',
  'customers',
  'reportsSupplier',
  'documents',
];

// Compliance customer view — customer sees only what they need to complete
const COMPLIANCE_CUSTOMER_ORDER: NavKey[] = [
  'documents',
];

// Logistics users — same sidebar as customers. The Call Off sub-tab inside
// Purchasing remains dispatched/delivered-only via CallOffViewSet.get_queryset()
// in the backend, so no separate nav entry is needed.
const LOGISTICS_ORDER: NavKey[] = [
  'dashboard',
  'salesOrders',
  'calendarCustomer',
  'qualityCustomer',
  'suppliers',
  'documents',
  'newsReleaseCustomer',
  'stockLevels',
];


// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export type PortalMode = 'supplier' | 'customer';

export interface BuildNavigationOptions {
  /** Which portal the user sees: supplier or customer */
  portalMode: PortalMode;
  /** Whether the application is running in Compliance mode (CR-4143) */
  isCompliance?: boolean;
  /** Whether customer is in Supplier Portal supplier mode (managing suppliers) */
  isSupplierPortalSupplier?: boolean;
  /** Whether the user belongs to a logistics company (restricted view) */
  isLogistics?: boolean;
}

/**
 * Returns the ordered list of `NavItem` objects for the given portal mode.
 *
 * This replaces the hardcoded arrays that previously lived in `Sidebar.tsx`
 * (`supplierNavigation`, `customerNavigation`, etc.).
 */
export function buildNavigation({ portalMode, isCompliance, isLogistics }: BuildNavigationOptions): NavItem[] {
  let order: NavKey[];

  if (isLogistics) {
    order = LOGISTICS_ORDER;
  } else if (isCompliance) {
    order = portalMode === 'customer' ? COMPLIANCE_CUSTOMER_ORDER : COMPLIANCE_SUPPLIER_ORDER;
  } else {
    order = portalMode === 'customer' ? STANDARD_CUSTOMER_ORDER : STANDARD_SUPPLIER_ORDER;
  }

  return order.map(key => {
    const item = NAV_ITEMS[key];
    const navItem: NavItem = { ...item };
    const itemChildren = (item as { children?: NavItem[] }).children;
    if (itemChildren) {
      navItem.children = itemChildren.map(child => ({ ...child }));
    }
    return navItem;
  });
}
